/**
 * «Финансы → Расходы»: выплаты касс CRM — что из них в P&L, а что нет.
 * Наличные идут в P&L сами, по статье выплаты; безнал — только через
 * связанную операцию выписки. Сводка по месяцам и статусам, клик по числу —
 * список этих выплат ниже; у безнала — его операция банка или похожие.
 *
 * Спека: `docs/specs/2026-10-02-expense-coverage-design.md`.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { DateRangePicker, withPeriod } from "@/components/DateRangePicker";
import { NoAccess } from "@/components/NoAccess";
import { KASSA, PNL, useCan } from "@/lib/access";
import {
  type Bucket,
  type Coverage,
  type CoverageFilter,
  type CoverageOperation,
  type CoveragePage,
  type CoverageRow,
  type CoverageStatus,
  loadCoverage,
  loadCoverageRows,
} from "@/lib/expenses";
import type { KassaKind } from "@/lib/kassa";
import { money, monthLabel } from "@/lib/pnl";
import { Pager } from "@/pages/KassaPage";

const TH = "whitespace-nowrap border-b border-border px-2.5 py-2 text-right align-bottom";
const CELL = "whitespace-nowrap border-b border-border px-2.5 py-1 text-right tabular-nums";
const NAME = "border-b border-border px-3 py-1 text-left";
const EDGE = "border-l border-border";
const TD = "border-b border-border px-2.5 py-1.5 align-top";
const LIMIT = 100;

/** Какие статусы бывают у вида кассы — столбцы сводки. Внутренние — только «всего». */
const COLUMNS: Record<KassaKind, CoverageStatus[]> = {
  cash: ["pnl", "unassigned", "outside"],
  document: ["pnl", "unassigned", "outside", "similar", "missing"],
  internal: [],
};

/** Что требует взгляда: без статьи — разнести; не связана — найти операцию. */
const TONE: Partial<Record<CoverageStatus, string>> = {
  unassigned: "text-warn",
  similar: "text-warn",
  missing: "text-err",
};

const PARAMS = ["kind", "status", "month", "kassa_id", "type", "offset"] as const;

function day(iso: string): string {
  return iso.slice(0, 10).split("-").reverse().join(".");
}

function add(target: Bucket, from: Bucket | undefined): Bucket {
  return from ? { count: target.count + from.count, sum: String(Number(target.sum) + Number(from.sum)) } : target;
}

export function ExpensesPage() {
  const pnl = useCan(PNL);
  const kassa = useCan(KASSA);
  return pnl && kassa ? <ExpensesScreen /> : <NoAccess what="расходам (нужны права «P&L и операции» и «Расходы касс»)" />;
}

function ExpensesScreen() {
  const [params, setParams] = useSearchParams();
  const [report, setReport] = useState<Coverage | null>(null);
  const [page, setPage] = useState<CoveragePage | null>(null);
  const [failure, setFailure] = useState("");

  const from = params.get("date_from") ?? "";
  const to = params.get("date_to") ?? "";
  const filter: CoverageFilter = {
    kind: (params.get("kind") as KassaKind | null) ?? undefined,
    status: (params.get("status") as CoverageFilter["status"] | null) ?? undefined,
    month: params.get("month") ?? undefined,
    kassa_id: params.get("kassa_id") ? Number(params.get("kassa_id")) : undefined,
    type: params.get("type") ?? undefined,
    date_from: from || undefined,
    date_to: to || undefined,
    limit: LIMIT,
    offset: Number(params.get("offset") ?? 0),
  };
  const picked = PARAMS.some((k) => k !== "offset" && params.get(k) !== null);

  useEffect(() => {
    setFailure("");
    setReport(null);
    loadCoverage({ date_from: from || undefined, date_to: to || undefined })
      .then(setReport)
      .catch((error: Error) => setFailure(error.message));
  }, [from, to]);

  const listKey = params.toString();
  useEffect(() => {
    if (!picked) {
      setPage(null);
      return;
    }
    setPage(null);
    loadCoverageRows(filter)
      .then(setPage)
      .catch((error: Error) => setFailure(error.message));
    // Фильтр целиком в адресе: его строка — и есть зависимость.
  }, [listKey]);

  /** Выбрать выплаты: заменяет прежний отбор списка, период остаётся. */
  const pick = (next: Partial<Record<(typeof PARAMS)[number], string | undefined>>) => {
    const out = new URLSearchParams(params);
    for (const key of PARAMS) out.delete(key);
    for (const [key, value] of Object.entries(next)) if (value) out.set(key, value);
    setParams(out);
  };
  const isPicked = (kind: string, status: string | undefined, month: string | undefined) =>
    params.get("kind") === kind &&
    (params.get("status") ?? undefined) === status &&
    (params.get("month") ?? undefined) === month &&
    !params.get("kassa_id");

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:flex-none">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[12.5px] text-fg-muted">
        <DateRangePicker label="период" from={from} to={to} onChange={(f, t) => setParams(withPeriod(params, f, t))} />
        <span>
          Выплаты касс CRM по дню выдачи. Наличные идут в P&L сами, по статье выплаты. Безнал — только через
          связанную операцию выписки: не связана — в P&L её нет.
        </span>
      </div>
      {failure ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {failure}
        </div>
      ) : null}
      {report === null ? (
        failure ? null : <div className="text-[12.5px] text-fg-muted">…</div>
      ) : report.months.length === 0 ? (
        <div className="text-[12.5px] text-fg-muted">За период выплат касс нет.</div>
      ) : (
        <>
          <Summary report={report} onPick={pick} isPicked={isPicked} />
          <Unlinked report={report} onPick={pick} params={params} />
        </>
      )}
      {picked ? <List page={page} report={report} filter={filter} onOffset={(o) => {
        const next = new URLSearchParams(params);
        if (o) next.set("offset", String(o));
        else next.delete("offset");
        setParams(next);
      }} /> : null}
    </main>
  );
}

type Pick = (next: Partial<Record<(typeof PARAMS)[number], string | undefined>>) => void;

function Summary(props: {
  report: Coverage;
  onPick: Pick;
  isPicked: (kind: string, status: string | undefined, month: string | undefined) => boolean;
}) {
  const { report } = props;
  const name = Object.fromEntries(report.statuses.map((s) => [s.code, s]));
  const kinds = report.kinds.filter((k) => report.totals.some((t) => t.kind === k.code));
  const cell = (month: string | undefined, kind: KassaKind, status: CoverageStatus | undefined): Bucket => {
    const source = month ? report.cells.filter((c) => c.month === month) : report.totals;
    return source
      .filter((c) => c.kind === kind && (status === undefined || c.status === status))
      .reduce<Bucket>((acc, c) => add(acc, c), { count: 0, sum: "0" });
  };
  const num = (p: { month?: string; kind: KassaKind; status?: CoverageStatus; first?: boolean; strong?: boolean }) => {
    const b = cell(p.month, p.kind, p.status);
    const on = props.isPicked(p.kind, p.status, p.month);
    const tone = p.status && b.count ? (TONE[p.status] ?? "") : "";
    return (
      <td
        key={`${p.kind}-${p.status ?? "all"}`}
        className={`${CELL} ${p.first ? EDGE : ""} ${tone} ${p.strong ? "font-medium" : ""} ${
          b.count ? "cursor-pointer hover:bg-bg-hover" : "text-fg-faint"
        } ${on ? "bg-accent-dim" : ""}`}
        title={b.count ? `${b.count} шт. — показать` : undefined}
        onClick={b.count ? () => props.onPick({ kind: p.kind, status: p.status, month: p.month }) : undefined}
      >
        {b.count ? money(b.sum, true) : "—"}
        {b.count ? <div className="text-[10.5px] text-fg-faint">{b.count} шт.</div> : null}
      </td>
    );
  };
  return (
    <section data-annot="сводка расходов" className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
            <th className={`${NAME} align-bottom`} rowSpan={2}>
              месяц
            </th>
            {kinds.map((k) => (
              <th key={k.code} colSpan={COLUMNS[k.code].length + 1} className={`${TH} ${EDGE} text-center text-fg`}>
                {k.name}
              </th>
            ))}
          </tr>
          <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
            {kinds.map((k) => [
              <th key={`${k.code}-all`} className={`${TH} ${EDGE}`}>
                всего
              </th>,
              ...COLUMNS[k.code].map((s) => (
                <th key={`${k.code}-${s}`} className={`${TH} max-w-[110px] whitespace-normal`} title={name[s]?.hint}>
                  {name[s]?.name ?? s}
                </th>
              )),
            ])}
          </tr>
        </thead>
        <tbody>
          {report.months.map((m) => (
            <tr key={m}>
              <td className={`${NAME} whitespace-nowrap`}>{monthLabel(m)}</td>
              {kinds.map((k) => [
                num({ month: m, kind: k.code, first: true, strong: true }),
                ...COLUMNS[k.code].map((s) => num({ month: m, kind: k.code, status: s })),
              ])}
            </tr>
          ))}
          <tr className="bg-bg-subtle font-medium">
            <td className={NAME}>Итого</td>
            {kinds.map((k) => [
              num({ kind: k.code, first: true, strong: true }),
              ...COLUMNS[k.code].map((s) => num({ kind: k.code, status: s })),
            ])}
          </tr>
        </tbody>
      </table>
      <div className="grid gap-0.5 border-t border-border px-3 py-2 text-[11px] text-fg-muted">
        {report.statuses.map((s) => (
          <div key={s.code}>
            <span className={`font-medium ${TONE[s.code] ?? "text-fg"}`}>{s.name}</span> — {s.hint}
          </div>
        ))}
      </div>
    </section>
  );
}

/** Безнал без операции банка: касса × тип. Показывает, выписок какого счёта не хватает. */
function Unlinked(props: { report: Coverage; onPick: Pick; params: URLSearchParams }) {
  const groups = props.report.missing_groups;
  if (!groups.length) return null;
  return (
    <section data-annot="безнал без операции банка" className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
            <th className={NAME}>
              Безнал без операции банка — где
              <div className="font-normal">в P&L этих выплат нет; клик — список</div>
            </th>
            <th className={`${NAME} align-bottom`}>тип расхода CRM</th>
            <th className={TH}>штук</th>
            <th className={TH}>сумма</th>
            <th className={TH} title="Из них — с похожей операцией в выписке: выписка есть, получатель не узнан">
              есть похожая
            </th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const on =
              props.params.get("kassa_id") === String(g.kassa_id) && props.params.get("type") === g.type;
            return (
              <tr
                key={`${g.kassa_id}-${g.type}`}
                className={`cursor-pointer hover:bg-bg-hover ${on ? "bg-accent-dim" : ""}`}
                onClick={() =>
                  props.onPick({ kind: "document", status: "unlinked", kassa_id: String(g.kassa_id), type: g.type })
                }
              >
                <td className={NAME}>{g.kassa_name}</td>
                <td className={NAME}>{g.type || "(без типа)"}</td>
                <td className={CELL}>{g.count}</td>
                <td className={`${CELL} font-medium text-err`}>{money(g.sum, true)}</td>
                <td className={`${CELL} ${g.similar ? "text-warn" : "text-fg-faint"}`}>{g.similar || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function List(props: { page: CoveragePage | null; report: Coverage | null; filter: CoverageFilter; onOffset: (o: number) => void }) {
  const { page, filter } = props;
  const status = props.report?.statuses.find((s) => s.code === filter.status)?.name;
  const kind = props.report?.kinds.find((k) => k.code === filter.kind)?.name;
  const what = [
    kind,
    filter.status === "unlinked" ? "не связана" : status,
    filter.month ? monthLabel(filter.month) : null,
    filter.type ? `тип «${filter.type}»` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  if (page === null) return <div className="text-[12.5px] text-fg-muted">…</div>;
  return (
    <section data-annot="выплаты" className="flex flex-col gap-2">
      <div className="flex items-center gap-3 text-[12px] text-fg-muted">
        <span className="font-medium text-fg">{what}</span>
        <span>
          Выплат: {page.total} · на {money(page.sum)} ₽
        </span>
        <Pager total={page.total} offset={filter.offset ?? 0} onOffset={props.onOffset} />
      </div>
      <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
        <table className="w-full border-collapse text-[12.5px]">
          <thead>
            <tr className="bg-bg-subtle text-left text-[11px] font-medium text-fg-muted">
              <th className={TD}>дата</th>
              <th className={`${TD} text-right`}>сумма</th>
              <th className={TD}>касса</th>
              <th className={TD}>тип · получатель</th>
              <th className={TD}>комментарий</th>
              <th className={TD}>статья</th>
              <th className={TD}>в выписке</th>
            </tr>
          </thead>
          <tbody>
            {page.rows.map((row) => (
              <Row key={row.id} row={row} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Row({ row }: { row: CoverageRow }) {
  return (
    <tr data-annot={`выплата CRM ${row.crm_id}`} className="hover:bg-bg-hover">
      <td className={`${TD} whitespace-nowrap tabular-nums text-fg-muted`}>
        {day(row.date)}
        <div className="text-[10.5px] text-fg-faint">№{row.crm_id}</div>
      </td>
      <td className={`${TD} whitespace-nowrap text-right tabular-nums`}>{money(row.amount)}</td>
      <td className={`${TD} whitespace-nowrap text-[11.5px]`}>
        <div>{row.kassa_name}</div>
        <div className="text-fg-faint">
          {row.entrepreneur_name ?? ""}
          {row.location_name ? ` · ${row.location_name}` : ""}
        </div>
      </td>
      <td className={`${TD} text-[11.5px]`}>
        <div>{row.type || "—"}</div>
        <div className="text-fg-muted">{row.recipient_name ?? ""}</div>
        {row.card_mask || row.recipient_bank ? (
          <div className="text-fg-faint">
            {[row.card_mask, row.recipient_bank].filter(Boolean).join(" · ")}
          </div>
        ) : null}
      </td>
      <td className={`${TD} max-w-[260px] text-[11.5px] text-fg-muted`}>{row.comment}</td>
      <td className={`${TD} text-[11.5px]`}>
        <div className={row.article_name ? "" : "text-warn"}>{row.article_name ?? "Не разнесено"}</div>
        {row.crm_article_name ? <div className="text-fg-faint">CRM: {row.crm_article_name}</div> : null}
      </td>
      <td className={`${TD} text-[11.5px]`}>
        <Bank row={row} />
      </td>
    </tr>
  );
}

function Operation({ op }: { op: CoverageOperation }) {
  return (
    <div>
      <span className="tabular-nums">
        {day(op.date)} −{money(op.amount)}
      </span>{" "}
      <span className="text-fg-muted">«{op.counterparty_name || "без получателя"}»</span>
      <div className="text-fg-faint">
        {op.entrepreneur_name} · {op.bank_name} {op.account ?? ""} · {op.article_name}
      </div>
    </div>
  );
}

function Bank({ row }: { row: CoverageRow }) {
  if (row.kassa_kind === "cash") return <span className="text-fg-faint">наличные — мимо банка</span>;
  if (row.kassa_kind === "internal") return <span className="text-fg-faint">внутренняя касса</span>;
  if (row.operation) return <Operation op={row.operation} />;
  if (Number(row.amount) <= 0) return <span className="text-fg-faint">возврат в кассу</span>;
  if (!row.similar_count) return <span className="text-err">операции нет</span>;
  return (
    <div className="grid gap-1">
      <span className="text-warn">
        не связана; похожих: {row.similar_count}
        {row.similar_count > row.similar.length ? ` (показаны ${row.similar.length})` : ""}
      </span>
      {row.similar.map((op) => (
        <Operation key={op.id} op={op} />
      ))}
    </div>
  );
}
